import { RefObject, useCallback, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Control, FieldValues, useForm, UseFormRegister } from 'react-hook-form';
import { createColumnHelper } from '@tanstack/react-table';
import { SearchField, Table, useSnackbar } from '@weareopenr/component-library';
import { useQueryClient } from '@tanstack/react-query';
import EmptyState from '../../components/EmptyState';
import Burger from '../../assets/images/Burger';
import { Routes } from '../../routes';
import styles from './PriceSchemePricingTab.module.scss';
import { PriceInput } from '../PricingTablePriceInput/PricingTablePriceInput';
import { useProductPrices } from '../../hooks/useProductPrices';
import { PRICING_FORM_ID } from '../../constants';
import { getPricingChannels, IPricingChannel } from 'src/helpers/channels/getPricingChannels';
import SkeletonContainer from '../../components/SkeletonContainer';
import { usePriceBands } from 'src/hooks/usePriceBands';
import { emptySectionButtonText, emptySectionText } from './constants';
import { DELIMITER, generatePriceFormDefaults, submitPrices } from '../../helpers/priceTable';
import { IProductPrice } from 'src/types/productPricing.types';
import { useSetDefaults } from '../../hooks/useProductPrices/useProductPrices';
import { useDebounce } from '../../hooks/useDebounce/useDebounce';
import { useRules } from '../../hooks/useRules/useRules';
import { useExternalSystems } from 'src/hooks/useExternalSystems/useExternalSystems';
import { useMenus } from 'src/hooks/useMenus';
import { NoResultsFound } from '../NoResultsFound';
import { usePricingCategoryFilter } from 'src/hooks/usePricingCategoryFilter';
import { PriceSchemeFilters } from '../PriceSchemeFilters/PriceSchemeFilters';
import { usePricingCategories } from 'src/hooks/usePricingCategories/usePricingCategories';
import { IPricingCategory } from 'src/api/getPricingCategories/types';
import { PricingSavePortal } from '../PricingSavePortal';
import { usePriceSchemeFilters } from 'src/hooks/usePriceSchemeFilters';

type PricingTableRow = {
  name: string;
  portion: string;
  itemId: string;
  priceBandId: string | null;
  pricingCategoryId: string | null;
  ranges?: string;
  priceBands?: PricingTableRow[];
};

const columnHelper = createColumnHelper<PricingTableRow>();

const buildChannelColumn = (
  channel: Pick<IPricingChannel, 'extensionInstanceId' | 'extensionInstanceName'>,
  index: number,
  pricingForm: { control: Control<FieldValues>; register: UseFormRegister<FieldValues> },
  priceRules: any,
  tableContainerRef: RefObject<HTMLDivElement>,
  orderedFlatCategories: IPricingCategory[] | undefined,
) =>
  columnHelper.display({
    id: channel?.extensionInstanceId || `Channel-${index}`,
    header: () => <span title={channel.extensionInstanceName}>{channel.extensionInstanceName}</span>,
    size: 150,
    cell: (info) => {
      const { itemId, priceBandId, pricingCategoryId } = info.row.original;
      return (
        <PriceCell
          itemId={itemId}
          priceBandId={priceBandId}
          extensionInstanceId={channel.extensionInstanceId}
          control={pricingForm.control}
          register={pricingForm.register}
          priceRules={priceRules}
          tableContainerRef={tableContainerRef}
          categoryId={pricingCategoryId}
          pricingCategories={orderedFlatCategories}
        />
      );
    },
  });

const buildPriceBandRows = (
  item: { item_id: string; product_size?: { name: string }; ranges?: { name: string }[] },
  priceBands: { name: string; priceBandId: string }[],
  pricingCategoryId: string | null,
): PricingTableRow[] =>
  priceBands
    .filter((pb) => pb.name !== 'Default')
    .map((pb) => ({
      name: pb.name,
      itemId: item.item_id,
      priceBandId: pb.priceBandId,
      pricingCategoryId,
      portion: item.product_size?.name ?? '',
      ranges: item.ranges?.map((x) => x.name).join(', ') || '',
    }));

const PriceCell = ({
  itemId,
  priceBandId,
  extensionInstanceId = null,
  control,
  register,
  priceRules,
  tableContainerRef,
  categoryId,
  pricingCategories,
}: {
  itemId: string;
  priceBandId: string | null;
  extensionInstanceId?: string | null;
  control: any;
  register: any;
  priceRules: any;
  tableContainerRef: React.RefObject<HTMLDivElement>;
  categoryId?: string | null;
  pricingCategories?: IPricingCategory[];
}) => {
  const inputName = `${itemId}${DELIMITER}${extensionInstanceId}${DELIMITER}${priceBandId}`;
  return (
    <PriceInput
      inputName={inputName}
      control={control}
      register={register}
      priceRules={priceRules}
      tableContainerRef={tableContainerRef}
      categoryId={categoryId}
      pricingCategories={pricingCategories}
    />
  );
};

const PriceSchemePricingTab = ({ priceSchemeId }: { priceSchemeId: string }) => {
  const { data: externalSystems, isLoading: isExternalSystemsLoading } = useExternalSystems();
  const { priceBands, isLoading: isPriceBandsLoading } = usePriceBands(priceSchemeId);
  const { data: priceRules, isLoading: isRulesLoading } = useRules(priceSchemeId);
  const { orderedFlatCategories, isLoading: isCategoriesLoading } = usePricingCategories(priceSchemeId);

  const { showSnackbar } = useSnackbar();
  const navigate = useNavigate();
  const [searchValue, setSearchValue] = useState<string>('');
  const [searchMenus, setSearchMenus] = useState<string[]>([]);
  const {
    missingDefaultPriceFilter,
    setMissingDefaultPriceFilter,
    missingDefaultPrice,
    expandCollapseFilter,
    onExpandCollapseFilterChange,
    expanded,
    setExpanded,
  } = usePriceSchemeFilters();
  const debouncedSearchValue = useDebounce(searchValue);
  const { data: menus } = useMenus();
  const {
    searchPricingCategories,
    setSearchPricingCategories,
    hasPricingCategoryFilterFeature,
    pricingCategoryOptions,
  } = usePricingCategoryFilter(priceSchemeId);

  const queryClient = useQueryClient();

  const pricingChannels = useMemo(() => getPricingChannels(externalSystems), [externalSystems]);

  const fetchLimit = 50;
  const cacheTimeToClearOnUnmount = 0;
  const {
    data,
    isLoading: isPricesLoading,
    isFetchingNextPage,
    queryKey,
    hasNextPage,
    fetchNextPage,
  } = useProductPrices(priceSchemeId, fetchLimit, {
    cacheTime: cacheTimeToClearOnUnmount,
    searchParam: debouncedSearchValue,
    menus: searchMenus,
    pricingCategories: searchPricingCategories,
    missingDefaultPrice,
  });

  const products = useMemo(() => data?.pages.flatMap((page) => page.data.map((product) => product)) ?? [], [data]);

  const tableContainerRef = useRef<HTMLDivElement>(null);

  const defaultValues = useMemo(() => {
    return generatePriceFormDefaults(products, priceBands, pricingChannels);
    // Default values can only be set on initial render, so we can safely ignore the dependencies here
  }, []);

  const pricingForm = useForm<Record<string, string>>({ defaultValues });

  const onNewProducts = useCallback(
    (products: IProductPrice[]) => {
      const defaults = generatePriceFormDefaults(products, priceBands, pricingChannels);
      pricingForm.reset(defaults, { keepValues: true, keepDirtyValues: true });
    },
    [priceBands, pricingChannels],
  );

  useSetDefaults(onNewProducts, queryKey);

  const tableData = useMemo<PricingTableRow[]>(
    () =>
      products.map((item) => {
        const pricingCategoryId = item.pricing_category?.id ?? null;
        return {
          name: item.name,
          itemId: item.item_id,
          priceBandId: null,
          pricingCategoryId,
          portion: item.product_size?.name,
          ranges: item.ranges?.map((x) => x.name).join(', ') || '',
          priceBands: buildPriceBandRows(item, priceBands, pricingCategoryId),
        };
      }),
    [products, priceBands],
  );

  const columns = useMemo(
    () => [
      columnHelper.accessor('name', {
        size: 250,
        header: 'Product',
      }),
      columnHelper.accessor('portion', {
        size: 100,
        header: 'Portion',
      }),
      columnHelper.accessor('ranges', {
        size: 200,
        header: 'Ranges',
      }),
      columnHelper.display({
        header: 'Default',
        size: 150,
        cell: (info) => {
          const { itemId, priceBandId, pricingCategoryId } = info.row.original;
          return (
            <PriceCell
              itemId={itemId}
              priceBandId={priceBandId}
              control={pricingForm.control}
              register={pricingForm.register}
              priceRules={priceRules}
              tableContainerRef={tableContainerRef}
              categoryId={pricingCategoryId}
              pricingCategories={orderedFlatCategories}
            />
          );
        },
      }),
      ...pricingChannels.map((channel, index) =>
        buildChannelColumn(channel, index, pricingForm, priceRules, tableContainerRef, orderedFlatCategories),
      ),
    ],
    [pricingChannels, priceRules, orderedFlatCategories],
  );

  const columnHeadersToRightAlign = useMemo(() => {
    return [...pricingChannels.map((channel) => channel.extensionInstanceName), 'Default'];
  }, [pricingChannels]);

  const invalidate = async () => {
    await queryClient.invalidateQueries({ queryKey: queryKey });
  };

  // dirtyFields read lazily at submit time — avoids subscribing this component to form state
  const onSubmit = (data: Record<string, string>) =>
    submitPrices(
      pricingForm.formState.dirtyFields,
      products,
      pricingForm.reset,
      showSnackbar,
      invalidate,
      priceSchemeId,
    )(data);

  const isLoadingData =
    isPricesLoading || isRulesLoading || isPriceBandsLoading || isCategoriesLoading || isExternalSystemsLoading;
  const hasActiveFilters =
    !!debouncedSearchValue || searchMenus.length > 0 || searchPricingCategories.length > 0 || missingDefaultPrice;

  const emptyStateText = emptySectionText;

  return (
    <div className={styles['price-scheme-pricing-tab']}>
      <div className={styles['controls-row']}>
        <div className={styles['search-container']}>
          <SearchField
            placeholder={'Search'}
            onChange={(e) => {
              e.preventDefault();
              e.stopPropagation();
              setSearchValue(e.target.value);
            }}
            onKeyDown={(e) => {
              if (e.key === 'Enter') {
                e.preventDefault();
              }
            }}
            isDisabled={false}
          />
          <PriceSchemeFilters
            menus={menus}
            searchMenus={searchMenus}
            setSearchMenus={setSearchMenus}
            hasPricingCategoryFilterFeature={hasPricingCategoryFilterFeature}
            pricingCategoryOptions={pricingCategoryOptions}
            searchPricingCategories={searchPricingCategories}
            setSearchPricingCategories={setSearchPricingCategories}
            missingDefaultPriceFilter={missingDefaultPriceFilter}
            setMissingDefaultPriceFilter={setMissingDefaultPriceFilter}
            expandCollapseFilter={expandCollapseFilter}
            setExpandCollapseFilter={onExpandCollapseFilterChange}
          />
        </div>
        <PricingSavePortal control={pricingForm.control} reset={pricingForm.reset} />
      </div>

      <form id={PRICING_FORM_ID} className={styles['form-container']} onSubmit={pricingForm.handleSubmit(onSubmit)}>
        {isLoadingData ? (
          <SkeletonContainer hiddenTitle />
        ) : (
          <>
            {products?.length === 0 && !hasActiveFilters ? (
              <EmptyState
                emptySectionText={emptyStateText}
                buttonText={emptySectionButtonText}
                onClick={() => navigate(Routes.PRODUCT_LIBRARY)}
                Icon={Burger}
              />
            ) : (
              <div className={styles['table-container']}>
                <Table
                  // className={styles['table-fill-height']}
                  data={tableData}
                  columns={columns}
                  columnsToPin={['name']}
                  columnHeadersToRightAlign={columnHeadersToRightAlign}
                  getSubRows={(originalRow) => originalRow.priceBands}
                  tableContainerRef={tableContainerRef}
                  additionalConfig={{
                    state: { expanded },
                    onExpandedChange: setExpanded,
                    getRowId: (row) => `${row.itemId}-${row.priceBandId ?? 'default'}`,
                  }}
                  {...(hasActiveFilters && {
                    EmptyState: () => (
                      <div className={styles['no-results']}>
                        <NoResultsFound />
                      </div>
                    ),
                  })}
                  isFetchingNextPage={isFetchingNextPage}
                  hasNextPage={hasNextPage}
                  fetchNextPage={fetchNextPage}
                  virtualise
                />
              </div>
            )}
          </>
        )}
      </form>
    </div>
  );
};

export default PriceSchemePricingTab;
